import React, { useState } from 'react';
import { LicenseEnv } from '../types';
import { useAuth } from '../contexts/AuthContext';
import {
  getLicenseEnv,
  setLicenseEnv,
  hasLicenseToken,
  getLicenseBaseUrl,
  licenseLogin,
} from '../services/api';
import './EnvBadge.css';

interface EnvBadgeProps {
  // Notifies the parent when the active license env changes (so it can refresh data/URL display).
  onEnvChange?: (env: LicenseEnv) => void;
}

// Persistent PROD/DEV indicator for the License API. Click to toggle. Switching to an env
// without a stored token opens an inline login modal for that env ("re-login if missing").
const EnvBadge: React.FC<EnvBadgeProps> = ({ onEnvChange }) => {
  const { notifyAuthChanged } = useAuth();
  const [env, setEnv] = useState<LicenseEnv>(getLicenseEnv());
  const [pendingEnv, setPendingEnv] = useState<LicenseEnv | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [error, setError] = useState('');

  const applyEnv = (next: LicenseEnv) => {
    setLicenseEnv(next);
    setEnv(next);
    notifyAuthChanged();
    onEnvChange?.(next);
  };

  const toggle = () => {
    const next: LicenseEnv = env === 'PROD' ? 'DEV' : 'PROD';
    if (hasLicenseToken(next)) {
      applyEnv(next);
    } else {
      // Need to authenticate against this env first.
      setPendingEnv(next);
      setUsername('');
      setPassword('');
      setError('');
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingEnv) return;
    setLoggingIn(true);
    setError('');
    try {
      const ok = await licenseLogin(pendingEnv, { user_name: username, password });
      if (ok) {
        applyEnv(pendingEnv);
        setPendingEnv(null);
      } else {
        setError('Login failed for the selected environment.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.description || err?.message || 'Login failed.');
    } finally {
      setLoggingIn(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={`env-badge env-badge-${env.toLowerCase()}`}
        onClick={toggle}
        title={`License API: ${getLicenseBaseUrl(env)}\nClick to switch environment`}
      >
        <span className="env-badge-dot" />
        {env}
      </button>

      {pendingEnv && (
        <div className="modal-overlay">
          <div className="modal-content env-login-modal">
            <h2>Login to {pendingEnv}</h2>
            <p className="env-login-url">{getLicenseBaseUrl(pendingEnv)}</p>
            <form onSubmit={handleLogin} className="env-login-form">
              <input
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              {error && <div className="error-message">{error}</div>}
              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setPendingEnv(null)}
                  disabled={loggingIn}
                >
                  Cancel
                </button>
                <button type="submit" className="submit-button" disabled={loggingIn}>
                  {loggingIn ? 'Logging in…' : `Login to ${pendingEnv}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default EnvBadge;
